import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mdiAccount, mdiFileDocument, mdiHome } from '@mdi/js';
import { TableTitle } from 'common/components/tables';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import {
  FilterProp,
  FiltersContainer,
  FilterTypes,
  OptionsFilterComponentHooks,
  TextFilterComponentHooks,
  useFiltration,
} from 'common/components/table/filtering';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import { Currency } from 'common/classes/currency';
import {
  MollyWhitelabelCustomerStatuses,
  MollyWhitelabelCustomerSummary,
} from 'common/state/molly-whitelabel/actions';
import { allCountries, countryNameToCode } from 'common/configs/countries';
import { mollyWhitelabelCustomerSummariesRecipe } from 'common/state/molly-whitelabel/recipes';
import { defaultMollyWhitelabelCustomerSummariesState } from 'common/state/molly-whitelabel/states';
import {
  MollyWhitelabelCustomerSummariesParams,
  doGetMollyWhitelabelCustomerSummaries,
} from 'common/state/molly-whitelabel/thunkActions';
import Icon from '@mdi/react';
import styled, { useTheme } from '@mollybet/ui/dist/styled';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mollybet/ui';

export const UsernameLink = styled(Link)`
  width: fit-content;
  padding-bottom: 0.125rem;
  border-bottom: 1px dashed;
  cursor: pointer;
  color: inherit;
`;

export const CustomersTable = () => {
  const theme = useTheme();
  const dropdownRef = useRef();
  const [state, dispatch] = useProducerWithThunks(
    mollyWhitelabelCustomerSummariesRecipe,
    defaultMollyWhitelabelCustomerSummariesState
  );
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const updateTimeout = useRef<ReturnType<typeof setTimeout>>();

  const columns = useMemo(
    (): ColumnProp<MollyWhitelabelCustomerSummary>[] => [
      { key: 'contact_id', title: 'Id' },
      {
        key: 'key',
        title: 'KYC',
        valueFormatter: (row) => (
          <>
            <Tooltip
              content={`KYC ${row.kyc_submitted ? '' : 'not '}submitted`}
            >
              <Icon
                path={mdiFileDocument}
                size={1}
                color={row.kyc_submitted ? theme.primary : theme.background}
              />
            </Tooltip>
            <Tooltip
              content={`Identity ${
                row.kyc_status.identity_verified ? '' : 'not '
              }verified`}
            >
              <Icon
                path={mdiAccount}
                size={1}
                color={
                  row.kyc_status.identity_verified
                    ? theme.primary
                    : theme.background
                }
              />
            </Tooltip>
            <Tooltip
              content={`Address ${
                row.kyc_status.address_verified ? '' : 'not '
              }verified`}
            >
              <Icon
                path={mdiHome}
                size={1}
                color={
                  row.kyc_status.address_verified
                    ? theme.primary
                    : theme.background
                }
              />
            </Tooltip>
          </>
        ),
        sortValueGenerator: (row) =>
          (row.kyc_submitted ? 1 : 0) +
          (row.kyc_status.identity_verified ? 1 : 0) +
          (row.kyc_status.address_verified ? 1 : 0),
      },
      { key: 'country', title: 'Country' },
      {
        key: 'contact_username',
        title: 'Contact Username',
        valueFormatter: (row) => (
          <UsernameLink to={`/mollywl/customers/${row.contact_username}/`}>
            {row.contact_username}
          </UsernameLink>
        ),
      },
      {
        key: 'account_username',
        title: 'Account Username',
        valueFormatter: (row) => (
          <UsernameLink to={`/mollywl/customers/${row.account_username}/`}>
            {row.account_username}
          </UsernameLink>
        ),
      },
      { key: 'first_name', title: 'First Name' },
      { key: 'last_name', title: 'Last Name' },
      { key: 'status', title: 'Status' },
      {
        key: 'net_totals',
        title: 'Net Totals',
        valueFormatter: (row) =>
          row.net_totals
            ? Object.keys(row.net_totals).map((currency) => (
                <div key={currency}>
                  {new Currency(row.net_totals[currency], currency).toElement()}
                </div>
              ))
            : new Currency().toElement({ symbol: false }),
        sortValueGenerator: (row) =>
          Object.values(row.net_totals || {}).reduce(
            (acc, value) => acc + parseFloat(value),
            0
          ),
      },
      {
        key: 'net_transfers',
        title: 'Net Transfers',
        valueFormatter: (row) =>
          row.net_transfers
            ? Object.keys(row.net_transfers).map((currency) => (
                <div key={currency}>
                  {new Currency(
                    row.net_transfers[currency],
                    currency
                  ).toElement()}
                </div>
              ))
            : new Currency().toElement({ symbol: false }),
        sortValueGenerator: (row) =>
          Object.values(row.net_transfers || {}).reduce(
            (acc, value) => acc + parseFloat(value),
            0
          ),
      },
    ],
    [theme]
  );

  const filters = useMemo(
    (): FilterProp<MollyWhitelabelCustomerSummary>[] => [
      {
        key: 'country',
        label: 'Country',
        type: FilterTypes.OPTIONS,
        options: allCountries.map(([code, name]) => ({
          value: code,
          label: name,
        })),
        valueGenerator: (row) => countryNameToCode[row.country],
        inputProps: {
          menuPortalTarget: dropdownRef.current,
        },
      },
      {
        key: 'kyc_submitted',
        label: 'KYC Submitted',
        type: FilterTypes.OPTIONS,
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
        inputProps: {
          menuPortalTarget: dropdownRef.current,
        },
      },
      {
        key: 'status',
        label: 'status',
        type: FilterTypes.OPTIONS,
        options: Object.values(MollyWhitelabelCustomerStatuses).map(
          (status) => ({
            value: status,
            label: status,
          })
        ),
        inputProps: {
          menuPortalTarget: dropdownRef.current,
        },
      },
      {
        key: 'contact_username',
        label: 'Username',
        type: FilterTypes.FREE_TEXT,
      },
    ],
    [dropdownRef]
  );

  const [rows, filterComponents, filterHooks] = useFiltration(
    state.customers || [],
    filters
  );

  useEffect(() => {
    const filters: MollyWhitelabelCustomerSummariesParams = {
      limit: pageSize,
      offset: pageSize * (pageNo - 1),
    };

    filterHooks.country =
      filterHooks?.country as OptionsFilterComponentHooks<MollyWhitelabelCustomerSummary>;
    filterHooks.kyc_submitted =
      filterHooks?.kyc_submitted as OptionsFilterComponentHooks<MollyWhitelabelCustomerSummary>;
    filterHooks.status =
      filterHooks?.status as OptionsFilterComponentHooks<MollyWhitelabelCustomerSummary>;
    filterHooks.contact_username =
      filterHooks?.contact_username as TextFilterComponentHooks<MollyWhitelabelCustomerSummary>;

    if (filterHooks.country?.value?.value)
      filters.country = filterHooks.country.value.value.toString();
    if (filterHooks.kyc_submitted?.value?.value)
      filters.kyc_submitted = filterHooks.kyc_submitted.value
        .value as unknown as boolean;
    if (filterHooks.status?.value?.value)
      filters.status = filterHooks.status.value
        .value as MollyWhitelabelCustomerStatuses;
    if (filterHooks.contact_username?.value)
      filters.username = filterHooks.contact_username.value;

    clearTimeout(updateTimeout.current);
    updateTimeout.current = setTimeout(
      () => dispatch(doGetMollyWhitelabelCustomerSummaries(filters)),
      300
    );
  }, [dispatch, filterHooks, pageSize, pageNo]);

  return (
    <>
      <TableTitle icon={mdiAccount} title="Customers" />
      <FiltersContainer hooks={filterHooks}>
        KYC
        {filterComponents.country}
        <br />
        {filterComponents.kyc_submitted}
        <br />
        {filterComponents.status}
        <br />
        {filterComponents.contact_username}
      </FiltersContainer>
      <div ref={dropdownRef} />
      <br />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName="Customers"
        pagination={{
          count: state.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isSortable={false}
      />
    </>
  );
};
